import React, { useState, useEffect } from 'react';
import { useWBL } from '../../context/WBLContext';
import { 
  Shield, 
  Bell, 
  BarChart3, 
  Lock, 
  Eye, 
  EyeOff, 
  Moon, 
  School, 
  Info, 
  CheckCircle2,
  Clock,
  ArrowRight,
  ShieldCheck,
  ShieldAlert,
  Gavel,
  History,
  Mail,
  Smartphone,
  UserCheck,
  Building2,
  FileText,
  Clock3,
  Award,
  Sparkles,
  Sliders,
  Check,
  Volume2,
  VolumeX,
  Briefcase,
  KeyRound,
  Zap
} from 'lucide-react';
import { NotificationPreferences } from '../../types';
import { HeroBanner } from '../Common/HeroBanner';
import { motion } from 'motion/react';

export const SettingsPage: React.FC = () => {
  const { activeUser, updateUserProfile, grantFerpaConsent, logs, updateMyPassword } = useWBL();
  const [activeTab, setActiveTab] = useState<'security' | 'alerts' | 'usage' | 'legal'>('security');
  
  // Security State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Alerts State
  const defaultPrefs: NotificationPreferences = {
    standardAlerts: true,
    emailNotifications: true,
    inAppNotifications: true,
    instantEmailAlerts: true,
    dailyDigest: false,
    ferpaConsentAlerts: true,
    newUserRegistrationAlerts: true,
    partnerOrgAlerts: true,
    applicationAlerts: true,
    hoursApprovalAlerts: true,
    evaluationAlerts: true,
    systemSecurityAlerts: true,
    opportunityCreatedAlerts: true,
    nightlyBlackout: false,
    schoolHourBlackout: false,
    complianceNode: true,
  };

  const [prefs, setPrefs] = useState<NotificationPreferences>(() => ({
    ...defaultPrefs,
    ...(activeUser?.notificationPrefs || {})
  }));

  const [saveStatus, setSaveStatus] = useState<string | null>(null);

  useEffect(() => {
    if (activeUser?.notificationPrefs) {
      setPrefs({
        ...defaultPrefs,
        ...activeUser.notificationPrefs
      });
    }
  }, [activeUser]);

  const togglePref = async (key: keyof NotificationPreferences) => {
    const newPrefs: NotificationPreferences = { ...prefs, [key]: !prefs[key] };
    setPrefs(newPrefs);
    setSaveStatus('Saving changes...');
    if (activeUser) {
      try {
        await updateUserProfile(activeUser.uid, { notificationPrefs: newPrefs });
        setSaveStatus('Saved to profile');
        setTimeout(() => setSaveStatus(null), 2500);
      } catch (e) {
        setSaveStatus('Failed to save');
      }
    }
  };

  const applyPreset = async (type: 'all' | 'essential' | 'mute') => {
    let preset: NotificationPreferences;
    if (type === 'all') {
      preset = {
        standardAlerts: true,
        emailNotifications: true,
        inAppNotifications: true,
        instantEmailAlerts: true,
        dailyDigest: false,
        ferpaConsentAlerts: true,
        newUserRegistrationAlerts: true,
        partnerOrgAlerts: true,
        applicationAlerts: true,
        hoursApprovalAlerts: true,
        evaluationAlerts: true,
        systemSecurityAlerts: true,
        opportunityCreatedAlerts: true,
        nightlyBlackout: false,
        schoolHourBlackout: false,
        complianceNode: true,
      };
    } else if (type === 'essential') {
      preset = {
        ...prefs,
        standardAlerts: true,
        emailNotifications: true,
        inAppNotifications: true,
        instantEmailAlerts: true,
        dailyDigest: false,
        ferpaConsentAlerts: true,
        newUserRegistrationAlerts: true,
        partnerOrgAlerts: false,
        applicationAlerts: true,
        hoursApprovalAlerts: false,
        evaluationAlerts: false,
        systemSecurityAlerts: true,
        opportunityCreatedAlerts: false,
      };
    } else {
      preset = {
        ...prefs,
        emailNotifications: false,
        instantEmailAlerts: false,
      };
    }
    setPrefs(preset);
    setSaveStatus('Preset applied & saved');
    if (activeUser) {
      try {
        await updateUserProfile(activeUser.uid, { notificationPrefs: preset });
        setTimeout(() => setSaveStatus(null), 2500);
      } catch (e) {
        setSaveStatus('Failed to save');
      }
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    if (newPassword.length < 6) {
      setPasswordError("New password must be at least 6 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match. Please verify and retry.");
      return;
    }

    setIsUpdatingPassword(true);
    try {
      await updateMyPassword(newPassword, currentPassword);
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(false), 4000);
    } catch (err: any) {
      console.error("Password update error:", err);
      setPasswordError(err?.message || "Failed to update security credentials in authentication.");
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  if (!activeUser) return null;

  const TABS = [
    { id: 'security', label: 'SECURITY', icon: Shield },
    { id: 'alerts', label: 'ALERTS', icon: Bell },
    { id: 'usage', label: 'USAGE', icon: BarChart3 },
    ...((activeUser?.role !== 'student' && activeUser?.role !== 'organization') ? [{ id: 'legal', label: 'LEGAL', icon: Gavel }] : []),
  ];

  const formatEngagement = (minutes: number = 401) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return { h, m };
  };

  const { h, m } = formatEngagement(activeUser.totalMinutesOnPortal);

  const handleDownloadReport = () => {
    if (!activeUser) return;
    
    // Filter logs for this user related to engagement or general activity
    const userLogs = logs.filter(l => l.userEmail === activeUser.email);
    
    // Create CSV header
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "Date,Time,Action,Details\n";
    
    // Add log rows
    userLogs.forEach(log => {
      const date = new Date(log.timestamp).toLocaleDateString();
      const time = new Date(log.timestamp).toLocaleTimeString();
      csvContent += `"${date}","${time}","${log.action}","${log.details.replace(/"/g, '""')}"\n`;
    });
    
    // Add summary row
    csvContent += `\n"TOTAL ENGAGEMENT","","","${h} hours, ${m} minutes"\n`;
    
    // Trigger download
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Engagement_Report_${activeUser.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="flex flex-col w-full font-sans bg-[#f4f6f9] min-h-screen">
      <HeroBanner 
        title="Settings" 
        subtitle="Manage your security, alerts, and engagement" 
        pageKey="settings" 
      />

      <div className="w-[95%] max-w-7xl mx-auto mt-6 pb-12">
        <div className="flex flex-col lg:flex-row bg-white rounded-3xl overflow-hidden shadow-xl shadow-slate-200/50 border border-slate-200/60">
          
          {/* Sidebar */}
          <aside className="w-full lg:w-72 bg-[#f8fafc] p-6 lg:p-10 shrink-0 flex flex-col border-r border-slate-200/60">
            <div className="space-y-8">
              <div>
                              </div>

              <nav className="flex flex-row lg:flex-col overflow-x-auto lg:overflow-visible gap-1.5 pb-2 lg:pb-0 scrollbar-none">
                {TABS.map(tab => {
                    const isActive = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as any)}
                            className={`w-auto lg:w-full flex items-center justify-between px-5 py-4 rounded-2xl text-left transition-all shrink-0 ${
                                isActive 
                                ? 'bg-white text-[#0094c8] shadow-md shadow-slate-200/50 font-extrabold ring-1 ring-slate-200/50' 
                                : 'text-[#5a738e] hover:bg-slate-200/50 hover:text-[#011d3c]'
                            }`}
                        >
                            <div className="flex items-center">
                              <tab.icon className={`w-4 h-4 mr-3 ${isActive ? 'text-[#0094c8]' : 'text-slate-400'}`} />
                              <span className="text-[11px] font-black tracking-widest uppercase">{tab.label}</span>
                            </div>
                            {isActive && <div className="w-1.5 h-1.5 rounded-full bg-[#0094c8] ml-2 hidden lg:block" />}
                        </button>
                    );
                })}
              </nav>
            </div>
            
            <div className="mt-auto pt-10 hidden lg:block">
              <div className="p-4 bg-[#0094c8]/5 rounded-2xl border border-[#0094c8]/10">
                <p className="text-[10px] font-bold text-[#0094c8] uppercase tracking-wider mb-1">Compliance Node</p>
                <p className="text-[10px] text-slate-500 leading-relaxed font-medium">SC HB 3431 Verified Terminal</p>
              </div>
            </div>
          </aside>
          
          {/* Main Content */}
          <main className="flex-1 p-6 lg:p-12 flex flex-col bg-white">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="max-w-3xl"
            >
              {activeTab === 'security' && (
                <div className="space-y-8">
                  <div className="space-y-2">
                    <h2 className="text-2xl font-black tracking-tight text-[#011d3c] uppercase font-display">Security Protocol</h2>
                    <p className="text-sm text-slate-500 font-medium">Update your credentials to maintain account integrity.</p>
                  </div>

                  <form onSubmit={handlePasswordUpdate} className="space-y-6 max-w-md">
                    {passwordError && (
                      <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-2.5 text-red-700 text-xs font-medium animate-fade-in">
                        <ShieldAlert className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                        <div className="flex-1">{passwordError}</div>
                      </div>
                    )}

                    {passwordSuccess && (
                      <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start space-x-2.5 text-emerald-700 text-xs font-medium animate-fade-in">
                        <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                        <div className="flex-1">Password has been successfully updated in Firebase Authentication!</div>
                      </div>
                    )}

                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Current Password</label>
                        <div className="relative">
                          <input 
                            type={showCurrentPassword ? "text" : "password"} 
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0094c8]/20 focus:border-[#0094c8] outline-none transition-all font-medium"
                            placeholder="••••••••"
                            required
                          />
                          <button 
                            type="button"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          >
                            {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">New Password</label>
                        <div className="relative">
                          <input 
                            type={showNewPassword ? "text" : "password"} 
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0094c8]/20 focus:border-[#0094c8] outline-none transition-all font-medium"
                            placeholder="Min. 8 characters"
                            required
                          />
                          <button 
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          >
                            {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Confirm New Password</label>
                        <input 
                          type="password" 
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0094c8]/20 focus:border-[#0094c8] outline-none transition-all font-medium"
                          placeholder="Repeat new password"
                          required
                        />
                      </div>
                    </div>

                    <button 
                      type="submit"
                      disabled={isUpdatingPassword}
                      className="w-full py-4 bg-[#011d3c] text-white rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-[#022a57] transition-all flex items-center justify-center space-x-2 disabled:opacity-50 shadow-lg shadow-slate-900/10"
                    >
                      {isUpdatingPassword ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          <span>Processing...</span>
                        </>
                      ) : passwordSuccess ? (
                        <>
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Credentials Updated</span>
                        </>
                      ) : (
                        <>
                          <Lock className="w-4 h-4" />
                          <span>Update Security Access</span>
                        </>
                      )}
                    </button>
                  </form>
                </div>
              )}

              {activeTab === 'alerts' && (
                <div className="space-y-8">
                  {/* Header & Role Badge & Status */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-6">
                    <div>
                      <h2 className="text-2xl font-black tracking-tight text-[#011d3c] uppercase font-display">Notification & Alert Preferences</h2>
                      <p className="text-xs text-slate-500 font-medium mt-1">
                        Select exactly which notifications, email dispatches, and compliance triggers you receive.
                      </p>
                    </div>

                    {/* Auto-save notification feedback */}
                    <div className="flex items-center gap-2 shrink-0">
                      {saveStatus ? (
                        <div className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl text-xs font-bold animate-pulse">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          <span>{saveStatus}</span>
                        </div>
                      ) : (
                        <div className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-slate-50 text-slate-500 border border-slate-200 rounded-xl text-xs font-medium">
                          <ShieldCheck className="w-3.5 h-3.5 text-slate-400" />
                          <span>Auto-synced with profile</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quick Action Presets */}
                  <div className="bg-slate-50/80 p-4 rounded-2xl border border-slate-200/80 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center space-x-2.5">
                      <Sliders className="w-4 h-4 text-[#0094c8]" />
                      <span className="text-xs font-black uppercase tracking-wider text-slate-700">Quick Presets:</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                      <button
                        type="button"
                        onClick={() => applyPreset('all')}
                        className="flex-1 sm:flex-none px-3 py-1.5 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-xl text-[11px] font-bold uppercase tracking-wider transition-all shadow-3xs cursor-pointer"
                      >
                        Enable All
                      </button>
                      <button
                        type="button"
                        onClick={() => applyPreset('essential')}
                        className="flex-1 sm:flex-none px-3 py-1.5 bg-white hover:bg-slate-100 text-[#0094c8] border border-[#0094c8]/30 rounded-xl text-[11px] font-bold uppercase tracking-wider transition-all shadow-3xs cursor-pointer"
                      >
                        Essential Only
                      </button>
                      <button
                        type="button"
                        onClick={() => applyPreset('mute')}
                        className="flex-1 sm:flex-none px-3 py-1.5 bg-white hover:bg-rose-50 text-rose-600 border border-rose-200 rounded-xl text-[11px] font-bold uppercase tracking-wider transition-all shadow-3xs cursor-pointer"
                      >
                        Mute Emails
                      </button>
                    </div>
                  </div>



                  {/* Section 2: Administrative & System Event Triggers */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <ShieldAlert className="w-4 h-4 text-emerald-600" />
                        <h3 className="text-xs font-black uppercase tracking-wider text-slate-900">
                          {activeUser.role === 'superadmin' || activeUser.role === 'regional_admin' ? 'Administrative & Compliance Events' : 'Portal Activity Alerts'}
                        </h3>
                      </div>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                        Tailored for your role
                      </span>
                    </div>

                    <div className="space-y-3">
                      {/* 1. FERPA Statutory Consent Releases (CRITICAL for Superadmin & Admin) */}
                      <div className="flex items-start sm:items-center justify-between p-4 sm:p-5 bg-gradient-to-r from-emerald-50/50 via-white to-white rounded-2xl border border-emerald-200/80 hover:border-emerald-300 hover:shadow-xs transition-all gap-4">
                        <div className="flex items-start space-x-3.5 min-w-0">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border transition-colors ${
                            prefs.ferpaConsentAlerts !== false ? 'bg-emerald-500 text-white border-emerald-600 shadow-xs' : 'bg-slate-100 text-slate-400 border-slate-200'
                          }`}>
                            <ShieldCheck className="w-5 h-5" />
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-xs font-black uppercase tracking-wider text-slate-900">FERPA Statutory Consents & Parent Releases</h4>
                            <p className="text-[11px] text-slate-600 font-medium leading-relaxed mt-0.5">
                              Alerts when a parent/guardian electronically executes the FERPA disclosure consent release for a student placement. Includes student name, school campus, and signer verification.
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => togglePref('ferpaConsentAlerts')}
                          className={`w-11 h-6 rounded-full transition-all relative shrink-0 cursor-pointer ${prefs.ferpaConsentAlerts !== false ? 'bg-emerald-600' : 'bg-slate-200'}`}
                          aria-label="Toggle FERPA Consent Alerts"
                        >
                          <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${prefs.ferpaConsentAlerts !== false ? 'left-6 shadow-xs' : 'left-1'}`} />
                        </button>
                      </div>

                      {/* 2. New User Registrations & Verifications */}
                      {(activeUser.role === 'superadmin' || activeUser.role === 'regional_admin' || activeUser.role === 'advisor') && (
                        <div className="flex items-start sm:items-center justify-between p-4 sm:p-5 bg-white rounded-2xl border border-slate-200/80 hover:border-[#0094c8]/40 hover:shadow-xs transition-all gap-4">
                          <div className="flex items-start space-x-3.5 min-w-0">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border transition-colors ${
                              prefs.newUserRegistrationAlerts !== false ? 'bg-[#0094c8]/10 text-[#0094c8] border-[#0094c8]/20' : 'bg-slate-100 text-slate-400 border-slate-200'
                            }`}>
                              <UserCheck className="w-5 h-5" />
                            </div>
                            <div className="min-w-0">
                              <h4 className="text-xs font-black uppercase tracking-wider text-slate-900">New User Registrations & Verifications</h4>
                              <p className="text-[11px] text-slate-500 font-medium leading-relaxed mt-0.5">
                                Notifications when students, parents, or staff register on the portal or request fast-track profile verification.
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => togglePref('newUserRegistrationAlerts')}
                            className={`w-11 h-6 rounded-full transition-all relative shrink-0 cursor-pointer ${prefs.newUserRegistrationAlerts !== false ? 'bg-[#0094c8]' : 'bg-slate-200'}`}
                            aria-label="Toggle User Registration Alerts"
                          >
                            <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${prefs.newUserRegistrationAlerts !== false ? 'left-6 shadow-xs' : 'left-1'}`} />
                          </button>
                        </div>
                      )}

                      {/* 3. Partner & Host Organization Onboarding */}
                      {(activeUser.role === 'superadmin' || activeUser.role === 'regional_admin') && (
                        <div className="flex items-start sm:items-center justify-between p-4 sm:p-5 bg-white rounded-2xl border border-slate-200/80 hover:border-[#0094c8]/40 hover:shadow-xs transition-all gap-4">
                          <div className="flex items-start space-x-3.5 min-w-0">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border transition-colors ${
                              prefs.partnerOrgAlerts !== false ? 'bg-purple-50 text-purple-600 border-purple-200' : 'bg-slate-100 text-slate-400 border-slate-200'
                            }`}>
                              <Building2 className="w-5 h-5" />
                            </div>
                            <div className="min-w-0">
                              <h4 className="text-xs font-black uppercase tracking-wider text-slate-900">Employer & Host Organization Onboarding</h4>
                              <p className="text-[11px] text-slate-500 font-medium leading-relaxed mt-0.5">
                                Alerts when business partners register new hosting organizations, upload MOUs, or request regional clearance.
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => togglePref('partnerOrgAlerts')}
                            className={`w-11 h-6 rounded-full transition-all relative shrink-0 cursor-pointer ${prefs.partnerOrgAlerts !== false ? 'bg-[#0094c8]' : 'bg-slate-200'}`}
                            aria-label="Toggle Partner Org Alerts"
                          >
                            <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${prefs.partnerOrgAlerts !== false ? 'left-6 shadow-xs' : 'left-1'}`} />
                          </button>
                        </div>
                      )}

                      {/* 4. Internship & Placement Applications */}
                      <div className="flex items-start sm:items-center justify-between p-4 sm:p-5 bg-white rounded-2xl border border-slate-200/80 hover:border-[#0094c8]/40 hover:shadow-xs transition-all gap-4">
                        <div className="flex items-start space-x-3.5 min-w-0">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border transition-colors ${
                            prefs.applicationAlerts !== false ? 'bg-sky-50 text-sky-600 border-sky-200' : 'bg-slate-100 text-slate-400 border-slate-200'
                          }`}>
                            <FileText className="w-5 h-5" />
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-xs font-black uppercase tracking-wider text-slate-900">Internship & Placement Applications</h4>
                            <p className="text-[11px] text-slate-500 font-medium leading-relaxed mt-0.5">
                              {activeUser.role === 'student' ? 'Updates on your submitted internship and career experience applications.' :
                               activeUser.role === 'organization' ? 'Instant alerts when students submit applications to your open positions.' :
                               'Notifications when students submit applications, interview schedules, or placement acceptances.'}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => togglePref('applicationAlerts')}
                          className={`w-11 h-6 rounded-full transition-all relative shrink-0 cursor-pointer ${prefs.applicationAlerts !== false ? 'bg-[#0094c8]' : 'bg-slate-200'}`}
                          aria-label="Toggle Application Alerts"
                        >
                          <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${prefs.applicationAlerts !== false ? 'left-6 shadow-xs' : 'left-1'}`} />
                        </button>
                      </div>

                      {/* 5. Work-Based Learning Timesheets & Hours */}
                      <div className="flex items-start sm:items-center justify-between p-4 sm:p-5 bg-white rounded-2xl border border-slate-200/80 hover:border-[#0094c8]/40 hover:shadow-xs transition-all gap-4">
                        <div className="flex items-start space-x-3.5 min-w-0">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border transition-colors ${
                            prefs.hoursApprovalAlerts !== false ? 'bg-teal-50 text-teal-600 border-teal-200' : 'bg-slate-100 text-slate-400 border-slate-200'
                          }`}>
                            <Clock3 className="w-5 h-5" />
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-xs font-black uppercase tracking-wider text-slate-900">Work Hours & Timesheet Submissions</h4>
                            <p className="text-[11px] text-slate-500 font-medium leading-relaxed mt-0.5">
                              Alerts for logged student training hours, supervisor sign-offs, and timesheet approvals.
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => togglePref('hoursApprovalAlerts')}
                          className={`w-11 h-6 rounded-full transition-all relative shrink-0 cursor-pointer ${prefs.hoursApprovalAlerts !== false ? 'bg-[#0094c8]' : 'bg-slate-200'}`}
                          aria-label="Toggle Hours Approval Alerts"
                        >
                          <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${prefs.hoursApprovalAlerts !== false ? 'left-6 shadow-xs' : 'left-1'}`} />
                        </button>
                      </div>

                      {/* 6. Evaluations & Performance Milestones */}
                      <div className="flex items-start sm:items-center justify-between p-4 sm:p-5 bg-white rounded-2xl border border-slate-200/80 hover:border-[#0094c8]/40 hover:shadow-xs transition-all gap-4">
                        <div className="flex items-start space-x-3.5 min-w-0">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border transition-colors ${
                            prefs.evaluationAlerts !== false ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-slate-100 text-slate-400 border-slate-200'
                          }`}>
                            <Award className="w-5 h-5" />
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-xs font-black uppercase tracking-wider text-slate-900">Evaluations & Competency Milestones</h4>
                            <p className="text-[11px] text-slate-500 font-medium leading-relaxed mt-0.5">
                              Notifications when mid-term or final workplace evaluations are submitted by supervisors.
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => togglePref('evaluationAlerts')}
                          className={`w-11 h-6 rounded-full transition-all relative shrink-0 cursor-pointer ${prefs.evaluationAlerts !== false ? 'bg-[#0094c8]' : 'bg-slate-200'}`}
                          aria-label="Toggle Evaluation Alerts"
                        >
                          <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${prefs.evaluationAlerts !== false ? 'left-6 shadow-xs' : 'left-1'}`} />
                        </button>
                      </div>

                      {/* 7. New Opportunity Postings Published */}
                      <div className="flex items-start sm:items-center justify-between p-4 sm:p-5 bg-white rounded-2xl border border-slate-200/80 hover:border-[#0094c8]/40 hover:shadow-xs transition-all gap-4">
                        <div className="flex items-start space-x-3.5 min-w-0">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border transition-colors ${
                            prefs.opportunityCreatedAlerts !== false ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-slate-100 text-slate-400 border-slate-200'
                          }`}>
                            <Briefcase className="w-5 h-5" />
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-xs font-black uppercase tracking-wider text-slate-900">Opportunity Postings & Status Changes</h4>
                            <p className="text-[11px] text-slate-500 font-medium leading-relaxed mt-0.5">
                              Alerts when new internships, apprenticeships, or career experiences are created and published in your region.
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => togglePref('opportunityCreatedAlerts')}
                          className={`w-11 h-6 rounded-full transition-all relative shrink-0 cursor-pointer ${prefs.opportunityCreatedAlerts !== false ? 'bg-[#0094c8]' : 'bg-slate-200'}`}
                          aria-label="Toggle Opportunity Created Alerts"
                        >
                          <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${prefs.opportunityCreatedAlerts !== false ? 'left-6 shadow-xs' : 'left-1'}`} />
                        </button>
                      </div>

                      {/* 8. System Security & Administrative Ledger (Superadmin / Regional Admin) */}
                      {(activeUser.role === 'superadmin' || activeUser.role === 'regional_admin') && (
                        <div className="flex items-start sm:items-center justify-between p-4 sm:p-5 bg-white rounded-2xl border border-slate-200/80 hover:border-[#0094c8]/40 hover:shadow-xs transition-all gap-4">
                          <div className="flex items-start space-x-3.5 min-w-0">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border transition-colors ${
                              prefs.systemSecurityAlerts !== false ? 'bg-rose-50 text-rose-600 border-rose-200' : 'bg-slate-100 text-slate-400 border-slate-200'
                            }`}>
                              <KeyRound className="w-5 h-5" />
                            </div>
                            <div className="min-w-0">
                              <h4 className="text-xs font-black uppercase tracking-wider text-slate-900">Security Audits & Administrative Role Changes</h4>
                              <p className="text-[11px] text-slate-500 font-medium leading-relaxed mt-0.5">
                                Audit ledger alerts when administrator roles are reassigned, credentials are reset, or tenant settings are altered.
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => togglePref('systemSecurityAlerts')}
                            className={`w-11 h-6 rounded-full transition-all relative shrink-0 cursor-pointer ${prefs.systemSecurityAlerts !== false ? 'bg-[#0094c8]' : 'bg-slate-200'}`}
                            aria-label="Toggle Security Alerts"
                          >
                            <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${prefs.systemSecurityAlerts !== false ? 'left-6 shadow-xs' : 'left-1'}`} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section 3: Quiet Hours & Digital Wellbeing */}
                  <div className="space-y-4 pt-2 border-t border-slate-100">
                    <div className="flex items-center space-x-2">
                      <Moon className="w-4 h-4 text-slate-400" />
                      <h3 className="text-xs font-black uppercase tracking-wider text-slate-900">Quiet Hours & Digital Wellbeing</h3>
                    </div>

                    <div className="space-y-3">
                      {/* Nightly Blackout */}
                      <div className="flex items-center justify-between p-4 sm:p-5 bg-white rounded-2xl border border-slate-200/80 hover:border-[#0094c8]/30 transition-all">
                        <div className="flex items-center space-x-3.5">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${prefs.nightlyBlackout ? 'bg-indigo-50 text-indigo-600 border-indigo-200' : 'bg-slate-100 text-slate-400 border-slate-200'}`}>
                            <Moon className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="text-xs font-black uppercase tracking-wider text-slate-800">Nightly Blackout</h4>
                            <p className="text-[11px] text-slate-500 font-medium">10:00 PM – 6:00 AM Daily Silence</p>
                          </div>
                        </div>
                        <button 
                          type="button"
                          onClick={() => togglePref('nightlyBlackout')}
                          className={`w-11 h-6 rounded-full transition-all relative shrink-0 cursor-pointer ${prefs.nightlyBlackout ? 'bg-[#0094c8]' : 'bg-slate-200'}`}
                          aria-label="Toggle Nightly Blackout"
                        >
                          <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${prefs.nightlyBlackout ? 'left-6 shadow-xs' : 'left-1'}`} />
                        </button>
                      </div>

                      {/* School Hour Blackout */}
                      <div className="flex items-center justify-between p-4 sm:p-5 bg-white rounded-2xl border border-slate-200/80 hover:border-[#0094c8]/30 transition-all">
                        <div className="flex items-center space-x-3.5">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${prefs.schoolHourBlackout ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-slate-100 text-slate-400 border-slate-200'}`}>
                            <School className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="text-xs font-black uppercase tracking-wider text-slate-800">School Hour Blackout</h4>
                            <p className="text-[11px] text-slate-500 font-medium">8:00 AM – 3:00 PM Weekday Silence</p>
                          </div>
                        </div>
                        <button 
                          type="button"
                          onClick={() => togglePref('schoolHourBlackout')}
                          className={`w-11 h-6 rounded-full transition-all relative shrink-0 cursor-pointer ${prefs.schoolHourBlackout ? 'bg-[#0094c8]' : 'bg-slate-200'}`}
                          aria-label="Toggle School Hour Blackout"
                        >
                          <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${prefs.schoolHourBlackout ? 'left-6 shadow-xs' : 'left-1'}`} />
                        </button>
                      </div>
                    </div>

                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200/80 flex items-start space-x-3">
                      <ShieldCheck className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                      <p className="text-[11px] text-slate-500 font-medium leading-relaxed italic">
                        Notification preferences are stored directly to your user account profile and are synchronized across your devices in real time.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'usage' && (
                <div className="space-y-12">
                  <div className="space-y-2 border-b border-slate-100 pb-8">
                    <h2 className="text-2xl font-black tracking-tight text-[#011d3c] uppercase font-display">Portal Engagement</h2>
                    <p className="text-sm text-slate-500 font-medium">Audit your active time on the work-based learning platform.</p>
                  </div>

                  <div className="space-y-10">
                    <div className="space-y-3">
                      <h3 className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400 ml-1">Cumulative Engagement</h3>
                      <div className="flex items-baseline space-x-6">
                        <div className="text-6xl font-black text-[#011d3c] flex items-baseline">
                          {h}<span className="text-2xl text-slate-300 ml-2 mr-4 font-bold uppercase tracking-widest">hr</span> 
                          {m}<span className="text-2xl text-slate-300 ml-2 font-bold uppercase tracking-widest">min</span>
                        </div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-[#0094c8] bg-[#0094c8]/5 px-4 py-2 rounded-full border border-[#0094c8]/10 shadow-sm">Total Time on Portal</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="p-8 bg-[#0094c8]/5 rounded-3xl border border-[#0094c8]/10 space-y-4">
                        <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center border border-[#0094c8]/20 shadow-sm">
                          <ShieldCheck className="w-6 h-6 text-[#0094c8]" />
                        </div>
                        <div className="space-y-2">
                          <h4 className="text-xs font-black uppercase tracking-widest text-[#0094c8]">Privacy & Compliance</h4>
                          <p className="text-[11px] text-[#0094c8]/80 font-medium leading-relaxed">
                            This log represents your cumulative session time. We track this data solely to promote digital wellbeing and comply with SC HB 3431.
                          </p>
                        </div>
                      </div>

                      <div className="p-8 bg-white rounded-3xl border border-slate-200/80 border-dashed space-y-4">
                        <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center border border-slate-200 shadow-sm">
                          <Info className="w-6 h-6 text-slate-400" />
                        </div>
                        <div className="space-y-2">
                          <h4 className="text-xs font-black uppercase tracking-widest text-slate-800">System Behavior</h4>
                          <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
                            Session time is calculated every 60 seconds of active portal interaction. Background tabs are automatically excluded.
                          </p>
                        </div>
                      </div>
                    </div>

                    <button 
                      onClick={handleDownloadReport}
                      className="w-full p-6 bg-slate-900 rounded-3xl text-white flex items-center justify-between group cursor-pointer overflow-hidden relative border-0"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-[#0094c8]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="relative z-10 flex items-center space-x-4">
                        <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center">
                          <BarChart3 className="w-5 h-5 text-[#0094c8]" />
                        </div>
                        <div className="text-left">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Extended Analytics</p>
                          <h4 className="text-sm font-bold uppercase tracking-wider">Download Engagement Report</h4>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-white group-hover:translate-x-1 transition-all relative z-10" />
                    </button>
                  </div>
                </div>
              )}
              {activeTab === 'legal' && activeUser?.role !== 'student' && activeUser?.role !== 'organization' && (
                <div className="space-y-10">
                  <div className="space-y-2 border-b border-slate-100 pb-8">
                    <h2 className="text-2xl font-black tracking-tight text-[#011d3c] uppercase font-display">Legal Authorizations</h2>
                    <p className="text-sm text-slate-500 font-medium">Review and manage your formal consents and data privacy agreements.</p>
                  </div>

                  <div className="space-y-8">
                    {/* FERPA Consent Card */}
                    <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
                        <div className="bg-slate-50 p-6 border-b border-slate-100 flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="p-3 bg-white rounded-2xl border border-slate-200 shadow-sm">
                                    <ShieldAlert className={`w-6 h-6 ${activeUser?.ferpaConsent?.granted ? 'text-emerald-500' : 'text-amber-500'}`} />
                                </div>
                                <div>
                                    <h3 className="text-xs font-black uppercase tracking-widest text-slate-900">FERPA Disclosure Consent</h3>
                                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-tight">Family Educational Rights & Privacy Act</p>
                                </div>
                            </div>
                            <div className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest ${activeUser?.ferpaConsent?.granted ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-amber-50 text-amber-600 border border-amber-100'}`}>
                                {activeUser?.ferpaConsent?.granted ? 'Active Authorization' : 'Action Required'}
                            </div>
                        </div>
                        
                        <div className="p-8 space-y-6">
                            <p className="text-xs text-slate-600 leading-relaxed font-medium italic">
                                "I hereby authorize the disclosure of protected education records to Regional Workforce Administrators, Educational Oversight, and Prospective Professional Partners for legitimate Work-Based Learning purposes."
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                                    <Clock className="w-4 h-4 text-slate-400" />
                                    <div>
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Consent Date</p>
                                        <p className="text-[11px] font-bold text-slate-900">{activeUser?.ferpaConsent?.date ? new Date(activeUser.ferpaConsent.date).toLocaleDateString() : 'Never Recorded'}</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                                    <Info className="w-4 h-4 text-slate-400" />
                                    <div>
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Protocol Version</p>
                                        <p className="text-[11px] font-bold text-slate-900">v{activeUser?.ferpaConsent?.version || '1.0'}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tight max-w-sm">
                                    Revocation is effective immediately, but cannot retrieve data previously shared.
                                </p>
                                <button
                                    onClick={() => grantFerpaConsent(!activeUser?.ferpaConsent?.granted)}
                                    className={`px-8 py-3 rounded-xl font-black uppercase tracking-widest text-[10px] transition-all ${
                                        activeUser?.ferpaConsent?.granted
                                        ? 'bg-red-50 text-red-600 hover:bg-red-100'
                                        : 'bg-[#0094c8] text-white hover:bg-[#007ba6] shadow-lg shadow-[#0094c8]/20'
                                    }`}
                                >
                                    {activeUser?.ferpaConsent?.granted ? 'Revoke Authorization' : 'Grant Full Consent'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Data Minimization / Privacy Policy Info */}
                    <div className="flex items-start space-x-5 p-8 bg-slate-900 rounded-3xl text-white relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-12 opacity-5 group-hover:opacity-10 transition-opacity">
                            <ShieldCheck className="w-32 h-32" />
                        </div>
                        <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center border border-white/10 shrink-0 relative z-10">
                            <CheckCircle2 className="w-6 h-6 text-[#0094c8]" />
                        </div>
                        <div className="space-y-2 relative z-10">
                            <h4 className="text-xs font-black uppercase tracking-[0.2em] text-[#0094c8]">Privacy Protocol: High Defaults</h4>
                            <p className="text-xs text-slate-300 leading-relaxed font-medium">
                                In accordance with SC HB 3431, your account is set to maximum privacy by default. WBL Portal does not use your data for secondary marketing or to train third-party AI models.
                            </p>
                            <div className="flex items-center space-x-4 pt-2">
                                <div className="flex items-center space-x-1.5">
                                    <History className="w-3 h-3 text-slate-500" />
                                    <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Audit Trail Active</span>
                                </div>
                                <div className="flex items-center space-x-1.5">
                                    <Shield className="w-3 h-3 text-slate-500" />
                                    <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Encryption: AES-256</span>
                                </div>
                            </div>
                        </div>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </main>
        </div>
      </div>
    </div>
  );
};
